/**
 * +grid: the Waystone Grid's economy ops (the intents gridAllocate / gridRefund / gridRespec /
 * gridUnlock / gridAutoAllocate / gridKeyClaim; server-authoritative online). Each works on the
 * profile's one character and returns a new Profile (never mutates its input).
 */
import type { DungeonDef } from '@keystone/sim';
import { GRID_KEY_SOURCES, getContentBundle, getGrid } from '@keystone/content';
import { heroRole, withSpec } from './specs';
import {
  GRID_RESPEC_GOLD,
  GRID_RESPEC_LEVEL,
  GRID_SECRET_YD,
  emptyGridState,
  gridAllocBlocker,
  gridKeys,
  gridPoints,
  gridRefundBlocker,
  gridStateOf,
  gridUnlockBlocker,
  logGridKey,
  planGrid,
} from './grid';
import type { CharacterProgress, GridState, Profile } from './types';

export type GridFail = { ok: false; reason: string };
export type GridOp<X = object> = ({ ok: true; profile: Profile } & X) | GridFail;

const fail = (reason: string): GridFail => ({ ok: false, reason });

function hero(profile: Profile): { p: Profile; ch: CharacterProgress; state: GridState } | GridFail {
  const c = profile.character;
  if (!c) return fail('No character');
  if (!profile.characters[c.classId]) return fail('No character');
  const p = structuredClone(profile);
  const ch = p.characters[c.classId]!;
  const state = gridStateOf(ch);
  return { p, ch, state };
}

/** Gold a refund of `n` stars costs at this level (free below GRID_RESPEC_LEVEL). */
export function gridRefundCost(level: number, n: number): number {
  return level >= GRID_RESPEC_LEVEL ? GRID_RESPEC_GOLD * Math.max(0, n) : 0;
}

/**
 * Allocate stars in order (the planning preview's Apply): each must touch the allocation so far and
 * there must be a point for each. All or nothing.
 */
export function gridAllocate(profile: Profile, nodeIds: readonly string[]): GridOp<{ added: string[] }> {
  const h = hero(profile);
  if ('reason' in h) return h;
  const { p, ch, state } = h;
  const classId = ch.classId;
  const ids = [...new Set(nodeIds)];
  if (!ids.length) return fail('Nothing to allocate');
  const free = gridPoints(profile, classId).free;
  if (ids.length > free) return fail(free ? `Only ${free} point${free === 1 ? '' : 's'} to spend` : 'No grid points to spend');
  for (const id of ids) {
    const why = gridAllocBlocker(classId, state, id);
    if (why) return fail(why);
    state.nodes.push(id);
  }
  ch.grid = state;
  return { ok: true, profile: p, added: ids };
}

/** Refund one star (the rest must stay connected). Gold at level 10+. */
export function gridRefund(profile: Profile, nodeId: string): GridOp<{ cost: number }> {
  const h = hero(profile);
  if ('reason' in h) return h;
  const { p, ch, state } = h;
  const why = gridRefundBlocker(ch.classId, state, nodeId);
  if (why) return fail(why);
  const cost = gridRefundCost(ch.hero.level, 1);
  if ((p.currencies.gold ?? 0) < cost) return fail(`A refund costs ${cost} gold`);
  p.currencies.gold -= cost;
  state.nodes = state.nodes.filter((x) => x !== nodeId);
  ch.grid = state;
  return { ok: true, profile: p, cost };
}

/** Refund every star (opened locks stay open). Free below level 10, then GRID_RESPEC_GOLD per star. */
export function gridRespec(profile: Profile): GridOp<{ cost: number; refunded: number }> {
  const h = hero(profile);
  if ('reason' in h) return h;
  const { p, ch, state } = h;
  const n = state.nodes.length;
  if (!n) return fail('Nothing to refund');
  const cost = gridRefundCost(ch.hero.level, n);
  if ((p.currencies.gold ?? 0) < cost) return fail(`A full respec costs ${cost} gold`);
  p.currencies.gold -= cost;
  state.nodes = [];
  ch.grid = state;
  return { ok: true, profile: p, cost, refunded: n };
}

/** Open a sealed lock with a Grid Key (permanent; costs no point). */
export function gridUnlock(profile: Profile, lockId: string): GridOp<{ keys: number }> {
  const h = hero(profile);
  if ('reason' in h) return h;
  const { p, ch, state } = h;
  const why = gridUnlockBlocker(ch.classId, state, lockId);
  if (why) return fail(why);
  if (gridKeys(profile, ch.classId).held < 1) return fail('You need a Grid Key');
  state.locks.push(lockId);
  ch.grid = state;
  return { ok: true, profile: p, keys: gridKeys(p, ch.classId).held };
}

/** Spend every free point along the suggested path (from what is already allocated). */
export function gridAutoAllocate(profile: Profile): GridOp<{ added: string[] }> {
  const h = hero(profile);
  if ('reason' in h) return h;
  const { p, ch, state } = h;
  const free = gridPoints(profile, ch.classId).free;
  if (!free) return fail('No grid points to spend');
  const base = getContentBundle().classes.find((c) => c.id === ch.classId);
  const def = base ? withSpec(profile, base) : undefined;
  const added = planGrid(ch.classId, heroRole(profile), free, state, def);
  if (!added.length) return fail('Nothing left to suggest');
  state.nodes.push(...added);
  ch.grid = state;
  return { ok: true, profile: p, added };
}

/**
 * Claim the Grid Key of a key-bearing secret chest the hero just opened: the chest must be one of
 * GRID_KEY_SOURCES.secrets on `mapId`, the hero on that map and within GRID_SECRET_YD of it (the
 * reported position, checked against the content). Once per character.
 */
export function gridKeyClaim(profile: Profile, mapId: string, secretId: string, pos: { x: number; y: number }, dungeons: readonly DungeonDef[] = getContentBundle().dungeons): GridOp<{ source: string; keys: number }> {
  const h = hero(profile);
  if ('reason' in h) return h;
  const { p, ch } = h;
  const source = `secret:${mapId}:${secretId}`;
  if (!GRID_KEY_SOURCES.secrets.includes(`${mapId}:${secretId}`)) return fail('No key here');
  if (p.character!.mapId !== mapId) return fail('Not on that map');
  const sec = dungeons.find((d) => d.id === mapId)?.field?.secrets?.find((s) => s.id === secretId);
  if (!sec || !Number.isFinite(pos.x) || !Number.isFinite(pos.y) || Math.hypot(sec.pos.x - pos.x, sec.pos.y - pos.y) > GRID_SECRET_YD) return fail('Too far away');
  if (!logGridKey(ch, source)) return fail('Already claimed');
  return { ok: true, profile: p, source, keys: gridKeys(p, ch.classId).held };
}

/** Rare-kill keys for accepted field kills (in place on a working copy). Returns the sources newly claimed. */
export function claimRareKeys(ch: CharacterProgress, npcIds: readonly string[]): string[] {
  const out: string[] = [];
  for (const id of npcIds) if (GRID_KEY_SOURCES.rares.includes(id) && logGridKey(ch, `rare:${id}`)) out.push(`rare:${id}`);
  return out;
}

/** The suggested next stars for the hero (the planning preview's Suggested button; not applied). */
export function gridSuggestion(profile: Profile): string[] {
  const c = profile.character;
  const ch = c ? profile.characters[c.classId] : undefined;
  if (!c || !ch) return [];
  const free = gridPoints(profile, c.classId).free;
  const base = getContentBundle().classes.find((k) => k.id === c.classId);
  return planGrid(c.classId, heroRole(profile), free, gridStateOf(ch), base ? withSpec(profile, base) : undefined);
}

/** Node count check for tools (the grid in the bundle). */
export const gridSize = (): number => getGrid().nodes.length;

export { emptyGridState };
